import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseCtl, ServiceLocatorService } from '@ncs/ncs-lib';
import { ChartOptions } from 'chart.js';
import * as QRCode from 'qrcode';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Editor, toHTML, Toolbar } from 'ngx-editor';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dashboard-charts',
  templateUrl: './dashboard-charts.component.html',
  styleUrls: ['./dashboard-charts.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardChartsComponent extends BaseCtl implements OnDestroy {
  href: string | undefined;

  @Output() tabChanged = new EventEmitter<string>();
  @Input() tab_index: number | null = 0;
  @Input() attributesList: any = [];
  @Input() tittle: any;
  @Input() uiformId: number | null = 0;
  @Input() userstoryId: number | null = 0;
  @Input() data: any;
  @Input() parentUsecaseList: any = [];
  @Input() oneRecord: any = 0;
  @Input() formId: any = 0;
  @Input() appId: any = 0;
  @Input() loadDelay: number = 0;

  private rawDataCache: number[] = [];
  filteredData: any[] = [];
  uiformDataList: any[] = [];
  searchValues: any = {};
  public usecaseSchema: any = {};
  public applicationList = [];
  public usecaseList = [];
  public attrList: any = [];
  private defaultsSet = false;

  private htmlCache = new Map<string, SafeHtml>();
  private readonly MAX_CACHE_SIZE = 200;
  private destroy$ = new Subject<void>();
  public isLoading = false;
  public override imageTimestamp: number = Date.now();
  private _cachedFilteredAttrs: any[] | null = null;

  private static _requestCache = new Map<string, Promise<any>>();

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    super('', locator, route);
    let _self = this;
    locator.config.getProperty('endpoint.auth').then((property: string) => {
      _self.initApiEndpoint(property);
    });
  }

  
  private cachedGet(url: string): Promise<any> {
    const cacheKey = url.split(';')[0]; // strip jsessionid

    if (!DashboardChartsComponent._requestCache.has(cacheKey)) {
      const promise = new Promise<any>((resolve, reject) => {
        this.locator.http.get(url, (res: any, error: boolean) => {
          if (error) {
            DashboardChartsComponent._requestCache.delete(cacheKey);
            reject(error);
          } else {
            resolve(res);
          }
        });
      });
      DashboardChartsComponent._requestCache.set(cacheKey, promise);
    }

    return DashboardChartsComponent._requestCache.get(cacheKey)!;
  }

  override async ngOnInit() {
    let _self = this;
    _self.isLoading = true;
    _self.cdr.markForCheck();

    
    if (_self.loadDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, _self.loadDelay));
    }

    try {
      await _self.getUsecaseSchema();
      if (this.appId > 0) {
        await _self.getAppDetails(this.appId);
      }
    } catch (error) {
      console.error('Error initializing dashboard:', error);
      _self.isLoading = false;
      _self.cdr.markForCheck();
    }
  }

  public async getUsecaseSchema() {
    let _self = this;

    return new Promise<void>((resolve, reject) => {
      _self.locator.config
        .getProperty('endpoint.userstory')
        .then((property: string) => {

         
          const usecasePromise = _self.cachedGet(property + '/get/' + _self.userstoryId)
            .then((response: any) => {
              _self.usecaseSchema = response.result.data;
            });

          // attributes/list NOT cached — unique per card instance
          const attrPromise = new Promise<void>((res, rej) => {
            let attrUrl =
              _self.locator.config.get('endpoint.userstory.attributes') +
              '/list/' + _self.userstoryId;

            _self.locator.http.get(attrUrl, (response: any, error: boolean) => {
              if (error) { rej(error); return; }

              _self.attrList = response.result.data || [];
              _self._cachedFilteredAttrs = null;

              if (_self.attrList?.length > 0) {
                _self.attrList.forEach((attribute: any) => {
                  const matchingField = _self.attributesList?.find(
                    (field: any) => field.id === attribute.id
                  );
                  if (matchingField) {
                    attribute.label = matchingField.label;
                    attribute.listView = matchingField?.listView;
                  }
                  if (attribute?.depAttribute) {
                    _self.parentUsecaseList.push(attribute);
                  }
                });
              }
              res();
            });
          });

          Promise.all([usecasePromise, attrPromise])
            .then(() => {
              _self.setDefaultValues();
              _self.getUiformDataList();
              _self.cdr.markForCheck();
              resolve();
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  gotoForm() {
    this.forward('uiformlist/' + this.formId);
  }

  public getUiformDataList() {
    let _self = this;

    _self.isLoading = true;
    _self.cdr.markForCheck();

    let url = _self.getAPIBaseUrl() + '/search/' + _self.form.pageNo;
    let searchdata = {
      ...Object.fromEntries(
        Object.entries(_self.searchValues).filter(
          ([_, value]) => value !== null && !Number.isNaN(value) && value !== ''
        )
      ),     
                   
    };
    searchdata['asc']='DESC',
    searchdata['orderBy']='createdDatetime',
    searchdata['pageSize'] = _self.oneRecord == 1 ? '11' : '5';

    _self.locator.http.post(url, searchdata, function (res: any, error: boolean) {
      _self.isLoading = false;

      if (error) {
        console.error('Server Error:', error);
        _self.cdr.markForCheck();
        return;
      }

      _self.clearHtmlCache();
      _self._cachedFilteredAttrs = null;

      _self.uiformDataList = res.result['data'] || [];
      _self.filteredData = [..._self.uiformDataList];

      _self.cdr.markForCheck();
    });
  }

  public filterAttributes(): any {
    let _self = this;
    const allowedTypes = ['date', 'time', 'list'];

    if (!_self._cachedFilteredAttrs) {
      _self._cachedFilteredAttrs = _self.attrList.filter(
        (attr: any) =>
          (_self.attributesList?.some((field: any) => field.id === attr.id) &&
            allowedTypes?.includes(attr['type'])) ||
          _self.parentUsecaseList?.some(
            (parentAttr: any) => parentAttr.id === attr.id
          )
      );
    }

    return _self._cachedFilteredAttrs;
  }

  public async drawChart() {
    let _self = this;

    if (!_self.form.data?.collectionName || !_self.form.data?.code) {
      console.warn('Missing required data for chart');
      return;
    }

    let attr_code = _self.form.data.collectionName + '.' + _self.form.data.code;
    let fields = _self.filterAttributes().filter((e: any) => e.code === _self.form.data.code);

    return new Promise<void>((resolve, reject) => {
      _self.locator.config.getProperty('endpoint.chart').then((property: string) => {
        _self.initApiEndpoint(property);
        let url = property + '/value/' + attr_code;

        _self.locator.http.post(url, {}, function (res: any, error: boolean) {
          if (error) { console.error('Chart API Error:', error); reject(error); return; }

          let data = res.result['data'];
          let labels = Object.keys(data);
          let chartData = Object.values(data);
          const backgroundColors = _self.generateColors(chartData.length);

          _self.chartdata = {
            labels,
            datasets: [{
              label: fields[0]?.['label'] || 'Data',
              data: chartData,
              backgroundColor: backgroundColors,
            }],
          };

          if (_self.form.data.type == 'range') {
            _self.chartdata = _self.getNumericData(fields[0], _self.chartdata);
          }
          if (_self.form.data.showPercentage) {
            _self.chartdata.datasets[0].data = _self.convertToPercentages(_self.chartdata.datasets[0].data);
            _self.updateChartOptions(_self.form.data.showPercentage);
          }

          _self.cdr.markForCheck();
          resolve();
        });
      }).catch(reject);
    });
  }

  getAttributeById(fieldId: number) {
    return this.attrList.find((attr: any) => attr.id === fieldId && attr['system'] != 1);
  }

  getAPIBaseUrl(suburl?: string) {
    let updatedName = this.usecaseSchema['collectionName']?.replace(/\./g, '/') || '';
    let url = this.locator.config.get('endpoint.api.v1') + '/' + updatedName;
    if (suburl) url += '/' + suburl;
    return url;
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handled in ngOnInit
  }

  private async setDefaultValues() {
    if (this.defaultsSet) return;

    const filtered = this.filterAttributes();
    if (filtered && filtered.length > 0) {
      this.form.data['attributeId'] = filtered[0].id;
      this.form.data['type'] = 'value';
      this.defaultsSet = true;
      await this.getAttributesById(this.form.data['attributeId']);
    }
  }

  async getAttributesById(id: number) {
    let _self = this;

    return new Promise<void>((resolve, reject) => {
      _self.locator.config.getProperty('endpoint.userstory.attributes').then((property: string) => {
        // ✅ cachedGet: eliminates duplicate attributes/get calls
        _self.cachedGet(property + '/get/' + id).then((res: any) => {
          const userstoryId = res.result.data.userstoryId;
          _self.form.data.code = res.result.data.code;
          _self.form.data.attrtype = res.result.data.type;
          _self.form.data.label = res.result.data.label;

          _self.locator.config.getProperty('endpoint.userstory').then((property: string) => {
            // ✅ cachedGet: biggest duplicate offender — usecase/get called 45 times in HAR
            _self.cachedGet(property + '/get/' + userstoryId).then(async (res: any) => {
              _self.form.data.collectionName = res.result.data.collectionName;
              await _self.drawChart();
              resolve();
            }).catch((err: any) => { console.error('Userstory API Error:', err); reject(err); });
          }).catch(reject);
        }).catch((err: any) => { console.error('Attribute API Error:', err); reject(err); });
      }).catch(reject);
    });
  }

  public getString(val: any, type: string) {
    if (val) {
      if (type === 'object' || type === 'list') val = JSON.stringify(val);
      return val;
    }
    return '';
  }

  private generateColors(count: number): string[] {
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(i < this.colorPalette.length
        ? this.colorPalette[i]
        : `rgba(${Math.floor(Math.random()*255)},${Math.floor(Math.random()*255)},${Math.floor(Math.random()*255)},0.7)`
      );
    }
    return colors;
  }

  private getNumericData(field: any, data: any): any {
    let max_values = this.getMaxValue(field, data);
    const rangeSize = max_values / 4;
    const ranges = [
      { min: 0, max: rangeSize },
      { min: rangeSize, max: 2 * rangeSize },
      { min: 2 * rangeSize, max: 3 * rangeSize },
      { min: 3 * rangeSize, max: max_values },
    ];
    let counts = [0, 0, 0, 0];
    data.labels.forEach((value: any, index: number) => {
      if (value != null) {
        ranges.forEach((r, ri) => {
          if (value >= r.min && value <= r.max) counts[ri] += data.datasets[0].data[index];
        });
      }
    });
    data.labels = ranges.map(r => `${r.min} - ${r.max}`);
    data.datasets[0].data = counts;
    data.datasets[0].backgroundColors = this.generateColors(4);
    return data;
  }

  private getMaxValue(field: any, data: any): number {
    return data.labels.length > 0 ? Math.max(...data.labels) : 0;
  }

  private convertToPercentages(data: number[]): number[] {
    const total = data.reduce((sum, value) => sum + value, 0);
    this.rawDataCache = [...data];
    return total > 0 ? data.map((value) => (value / total) * 100) : data;
  }

  private updateChartOptions(showPercentage: boolean) {
    let _self = this;
    _self.chartOptions = {
      ..._self.chartOptions,
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: (value) => showPercentage ? value + '%' : value },
        },
      },
      plugins: {
        ..._self.chartOptions.plugins,
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              const dataIndex = tooltipItem.dataIndex;
              const value = dataset.data[dataIndex];
              const total = dataset.data.reduce((a: any, b: any) => a + b, 0);
              const percentage = ((value as number) / total) * 100;
              const rawValue = _self.rawDataCache[dataIndex];
              return showPercentage
                ? `${dataset.label}: ${rawValue} (${percentage.toFixed(2)}%)`
                : `${dataset.label}: ${rawValue}`;
            },
          },
        },
      },
    };
  }

  private colorPalette: string[] = [
    'rgba(45, 49, 66, 0.8)', 'rgba(75, 75, 109, 0.8)', 'rgba(29, 53, 87, 0.8)',
    'rgba(69, 123, 157, 0.8)', 'rgba(230, 57, 70, 0.8)', 'rgba(168, 218, 220, 0.8)',
    'rgba(29, 105, 122, 0.8)', 'rgba(120, 66, 18, 0.8)', 'rgba(50, 50, 50, 0.8)',
    'rgba(34, 40, 49, 0.8)', 'rgba(123, 31, 162, 0.8)', 'rgba(67, 160, 71, 0.8)',
    'rgba(255, 87, 34, 0.8)', 'rgba(244, 81, 30, 0.8)', 'rgba(103, 58, 183, 0.8)',
  ];

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    plugins: { legend: { display: true, position: 'top' } },
    scales: {
      x: { display: true },
      y: { display: true, beginAtZero: true },
    },
  };

  public chartdata: any = {
    labels: [],
    datasets: [{
      label: 'view',
      data: Array(3).fill(0).map(() => Math.round(Math.random() * 100)),
      backgroundColor: [],
    }],
  };

  public applogo: Number = 0;

  getAppDetails(appId: any) {
    // ✅ cachedGet: same appId hit x4 in HAR
    const url = this.locator.config.get('endpoint.application') + '/get/' + appId;
    return this.cachedGet(url).then((res: any) => {
      this.applogo = res.result.data['logo'];
      this.cdr.markForCheck();
    }).catch((error: any) => { console.error('App Details Error:', error); throw error; });
  }

  getAppDocThumbnail(skey: number) {
    if (!skey) skey = this.applogo ? Number(this.applogo) : 0;
    return this.locator.config.get('DOC_API') + 'th/' + skey + '?t=' + this.imageTimestamp;
  }

  getAppDoc(skey: number) {
    if (!skey) skey = this.applogo ? Number(this.applogo) : 0;
    return this.getDoc(skey);
  }

  getString1(value: string, type: string, timeType?: string): string {
    if (type === 'time' && value) {
      const [hours, minutes] = value.split(':').map(Number);
      if (timeType === '12') {
        const hour12 = hours % 12 || 12;
        return `${hour12}:${minutes.toString().padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
      }
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    return value;
  }

  getStringitime(value: string, type: string, timeType?: string): string {
    if (type === 'itime' && value) {
      const d = new Date(value);
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      if (timeType === '12') {
        const hour12 = hours % 12 || 12;
        return `${hour12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
      }
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return value;
  }

  getLocationObject(value: any): { lat: number; lng: number } | null {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        const cleaned = value
          .replace(/lat\s*:\s*/i, '"lat":')
          .replace(/lng\s*:\s*/i, '"lng":')
          .replace(/'/g, '"');
        return JSON.parse(cleaned);
      } catch (e) { return null; }
    }
    if (typeof value === 'object' && value.lat && value.lng) return value;
    return null;
  }

  getSanitizedHtml(html: any, cacheKey?: string): SafeHtml {
    if (!html && html !== 0 && html !== false)
      return this.sanitizer.bypassSecurityTrustHtml('');

    if (!cacheKey) cacheKey = this.generateCacheKey(html);
    if (this.htmlCache.has(cacheKey)) return this.htmlCache.get(cacheKey)!;

    let processedHtml = html;

    if (html && typeof html === 'object' && html.type === 'doc') {
      const objSize = JSON.stringify(html).length;
      if (objSize > 10000) {
        processedHtml = this.extractTextFromEditorDoc(html);
      } else {
        try { processedHtml = toHTML(html); }
        catch (err) { processedHtml = this.extractTextFromEditorDoc(html); }
      }
    }

    if (typeof processedHtml !== 'string') processedHtml = String(processedHtml ?? '');
    processedHtml = this.limitHtmlSize(processedHtml);

    const sanitized = this.sanitizer.bypassSecurityTrustHtml(processedHtml);

    if (cacheKey && processedHtml.length < 5000) {
      this.htmlCache.set(cacheKey, sanitized);
      if (this.htmlCache.size > this.MAX_CACHE_SIZE) {
        const firstKey = this.htmlCache.keys().next().value;
        if (firstKey !== undefined) this.htmlCache.delete(firstKey);
      }
    }

    return sanitized;
  }

  private generateCacheKey(html: any): string {
    if (!html) return 'empty';
    if (typeof html === 'string')
      return html.length < 50 ? `str-${html}` : `str-${this.hashCode(html)}`;
    if (typeof html === 'object') {
      try {
        const s = JSON.stringify(html);
        return `obj-${this.hashCode(s.length > 1000 ? s.substring(0, 1000) : s)}`;
      } catch (e) { return `obj-${Date.now()}-${Math.random()}`; }
    }
    return `val-${String(html)}`;
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return hash;
  }

  private extractTextFromEditorDoc(doc: any): string {
    if (!doc?.content || !Array.isArray(doc.content)) return '';
    let text = '';
    const extractText = (node: any) => {
      if (node.text) text += node.text + ' ';
      if (Array.isArray(node.content)) node.content.forEach(extractText);
    };
    doc.content.forEach(extractText);
    return text.length > 500 ? text.substring(0, 500) + '...' : text;
  }

  private limitHtmlSize(html: string, maxLength: number = 3000): string {
    if (html.length <= maxLength) return html;
    const truncated = html.substring(0, maxLength);
    const lastTagEnd = truncated.lastIndexOf('>');
    return lastTagEnd > maxLength * 0.8
      ? truncated.substring(0, lastTagEnd + 1) + '...'
      : truncated + '...';
  }

  clearHtmlCache(): void { this.htmlCache.clear(); }

  trackByFn(index: number, item: any): any { return item?.id ?? index; }

  openCodeInNewTab(type: string, value: string) {
    if (type === 'qr') {
      QRCode.toDataURL(value, { width: 200 }, (err, url) => {
        if (err) { console.error(err); return; }
        const win = window.open();
        win!.document.write(`<html><head><title>QR Code</title></head>
          <body style="display:flex;align-items:center;justify-content:center;height:100vh;">
          <img src="${url}" alt="QR Code"></body></html>`);
      });
    } else if (type === 'bar') {
      const win = window.open();
      win!.document.write(`<html><head><title>Barcode</title></head>
        <body style="display:flex;align-items:center;justify-content:center;height:100vh;">
        <svg id="barcode"></svg>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <script>JsBarcode("#barcode","${value}",{format:"CODE128",width:2,height:100,displayValue:true});</script>
        </body></html>`);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearHtmlCache();
    this._cachedFilteredAttrs = null;
    // _requestCache is static — intentionally kept for full page session
  }
}
